/**
 * Pure rules behind the user account dialog. The server enforces the same
 * constraints (UserResource, UserProcessor).
 */
import type { UserRole } from "@/api/types";

export const PASSWORD_MIN_LENGTH = 8;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Roles a super admin may grant through the API (User::ASSIGNABLE_ROLES). */
export const ASSIGNABLE_ROLES = [
  "ROLE_ADMIN",
  "ROLE_CATALOGER",
  "ROLE_CIRCULATION",
  "ROLE_ACQUISITION",
] as const satisfies readonly UserRole[];

export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

export const ROLE_DESCRIPTIONS: Record<AssignableRole, { label: string; description: string }> = {
  ROLE_ADMIN: {
    label: "Administrateur",
    description: "Gère les comptes et le paramétrage de son établissement.",
  },
  ROLE_CATALOGER: {
    label: "Catalogueur",
    description: "Crée et modifie les notices et les exemplaires.",
  },
  ROLE_CIRCULATION: {
    label: "Agent de prêt",
    description: "Prêts, retours, réservations et inscriptions des lecteurs.",
  },
  ROLE_ACQUISITION: {
    label: "Acquisitions",
    description: "Commandes, budgets et fournisseurs.",
  },
};

export interface UserDraft {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export type UserErrors = Partial<Record<keyof UserDraft, string | undefined>>;

export function validateUser(draft: UserDraft, isNew: boolean): UserErrors {
  const errors: UserErrors = {};

  if (draft.firstName.trim().length < 2) errors.firstName = "Au moins 2 caractères.";
  if (draft.lastName.trim().length < 2) errors.lastName = "Au moins 2 caractères.";
  if (!EMAIL_PATTERN.test(draft.email.trim())) errors.email = "Adresse e-mail invalide.";

  // On edit, an empty password keeps the current one
  if ((isNew || draft.password !== "") && draft.password.length < PASSWORD_MIN_LENGTH) {
    errors.password = `Au moins ${String(PASSWORD_MIN_LENGTH)} caractères.`;
  }

  return errors;
}
